import React, { useEffect, useMemo, useState } from "react";

const INSTALL_PROMPT_DISMISSED_KEY = "lcdf_install_prompt_dismissed_at";
const INSTALL_PROMPT_COOLDOWN = 1000 * 60 * 60 * 24 * 7;

const isPromptDismissedRecently = () => {
  const rawValue = window.localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);

  if (!rawValue) {
    return false;
  }

  const dismissedAt = Number(rawValue);

  if (!dismissedAt) {
    return false;
  }

  return Date.now() - dismissedAt < INSTALL_PROMPT_COOLDOWN;
};

const rememberDismiss = () => {
  window.localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, `${Date.now()}`);
};

const isStandaloneMode = () => (
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true
);

const getDeviceContext = () => {
  const userAgent = window.navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(userAgent);
  const isSafari = /safari/i.test(userAgent) && !/crios|fxios|edgios|chrome|android/i.test(userAgent);
  const isMobileViewport = window.matchMedia("(max-width: 767px)").matches;

  return {
    isIOS,
    isSafari,
    isMobileViewport,
  };
};

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [deviceContext, setDeviceContext] = useState(() => getDeviceContext());

  const canShowIosInstructions = useMemo(() => (
    deviceContext.isIOS &&
    deviceContext.isSafari &&
    deviceContext.isMobileViewport
  ), [deviceContext]);

  useEffect(() => {
    const refreshDeviceContext = () => {
      setDeviceContext(getDeviceContext());
    };

    const handleBeforeInstallPrompt = (event) => {
      if (isPromptDismissedRecently() || isStandaloneMode()) {
        return;
      }

      event.preventDefault();
      setDeferredPrompt(event);
      setIsVisible(true);
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setIsVisible(false);
      window.localStorage.removeItem(INSTALL_PROMPT_DISMISSED_KEY);
    };

    refreshDeviceContext();
    window.addEventListener("resize", refreshDeviceContext);
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("resize", refreshDeviceContext);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  useEffect(() => {
    if (isPromptDismissedRecently() || isStandaloneMode()) {
      setIsVisible(false);
      return;
    }

    if (deferredPrompt) {
      setIsVisible(true);
      return;
    }

    if (canShowIosInstructions) {
      setIsVisible(true);
    }
  }, [canShowIosInstructions, deferredPrompt]);

  const dismissPrompt = () => {
    rememberDismiss();
    setIsVisible(false);
    setDeferredPrompt(null);
  };

  const installApp = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  if (!isVisible || isStandaloneMode() || !deviceContext.isMobileViewport) {
    return null;
  }

  return (
    <div className="pwa-installPrompt" role="dialog" aria-live="polite" aria-label="Installation de l'application">
      <div className="pwa-installPrompt-content">
        <div className="pwa-installPrompt-copy">
          <strong>Installer l'application</strong>
          {deferredPrompt ? (
            <span>Ajoutez LCDF sur votre téléphone pour y accéder plus vite.</span>
          ) : (
            <span>Sur iPhone, utilisez le bouton Partager puis “Sur l'écran d'accueil”.</span>
          )}
        </div>
        <div className="pwa-installPrompt-actions">
          {deferredPrompt && (
            <button type="button" className="primary" onClick={installApp}>
              Installer
            </button>
          )}
          <button type="button" className="secondary" onClick={dismissPrompt}>
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
